// Chat Persistence Service for OMNIGATE
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  conversationId?: string;
}

export interface Conversation {
  id: string;
  title: string;
  messages: ChatMessage[];
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
}

export class ChatPersistence {
  private static readonly STORAGE_KEY = 'omnigate_chat_history';
  private static readonly CONVERSATIONS_KEY = 'omnigate_conversations';
  private static readonly MAX_CONVERSATIONS = 50;
  private static readonly MAX_MESSAGES_PER_CONVO = 1000;

  // Save message to current conversation
  public static saveMessage(message: Omit<ChatMessage, 'id' | 'timestamp'>): ChatMessage {
    const chatMessage: ChatMessage = {
      ...message,
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date().toISOString()
    };

    // Get current conversation or create new one
    let currentConvo = this.getCurrentConversation();
    if (!currentConvo) {
      currentConvo = this.createConversation('New Conversation');
    }

    // Add message to conversation
    currentConvo.messages.push(chatMessage);
    currentConvo.updatedAt = new Date().toISOString();

    // Trim old messages if needed
    if (currentConvo.messages.length > this.MAX_MESSAGES_PER_CONVO) {
      currentConvo.messages = currentConvo.messages.slice(-this.MAX_MESSAGES_PER_CONVO);
    }

    // Save updated conversation
    this.saveConversation(currentConvo);

    return chatMessage;
  }

  // Get current active conversation
  public static getCurrentConversation(): Conversation | null {
    const conversations = this.getAllConversations();
    return conversations.find(convo => convo.isActive) || null;
  }

  // Create new conversation
  public static createConversation(title: string): Conversation {
    const newConvo: Conversation = {
      id: `conv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      title,
      messages: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      isActive: true
    };

    // Deactivate other conversations
    this.deactivateAllConversations();

    // Save new conversation
    this.saveConversation(newConvo);

    return newConvo;
  }

  // Save conversation to storage
  private static saveConversation(conversation: Conversation): void {
    const conversations = this.getAllConversations();
    
    // Remove existing conversation with same ID
    const filtered = conversations.filter(c => c.id !== conversation.id);
    
    // Add updated conversation
    filtered.unshift(conversation);
    
    // Keep only recent conversations
    const trimmed = filtered.slice(0, this.MAX_CONVERSATIONS);
    
    localStorage.setItem(this.CONVERSATIONS_KEY, JSON.stringify(trimmed));
  }

  // Get all conversations
  public static getAllConversations(): Conversation[] {
    try {
      const stored = localStorage.getItem(this.CONVERSATIONS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Failed to parse conversations from localStorage:', e);
      return [];
    }
  }

  // Deactivate all conversations
  private static deactivateAllConversations(): void {
    const conversations = this.getAllConversations();
    const updated = conversations.map(convo => ({
      ...convo,
      isActive: false
    }));
    localStorage.setItem(this.CONVERSATIONS_KEY, JSON.stringify(updated));
  }

  // Set conversation as active
  public static setActiveConversation(conversationId: string): void {
    const conversations = this.getAllConversations();
    const updated = conversations.map(convo => ({
      ...convo,
      isActive: convo.id === conversationId
    }));
    localStorage.setItem(this.CONVERSATIONS_KEY, JSON.stringify(updated));
  }

  // Delete conversation
  public static deleteConversation(conversationId: string): void {
    const conversations = this.getAllConversations();
    const filtered = conversations.filter(c => c.id !== conversationId);
    localStorage.setItem(this.CONVERSATIONS_KEY, JSON.stringify(filtered));
  }

  // Clear all conversations
  public static clearAllConversations(): void {
    localStorage.removeItem(this.CONVERSATIONS_KEY);
  }

  // Export conversations to JSON
  public static exportConversations(): string {
    const conversations = this.getAllConversations();
    return JSON.stringify(conversations, null, 2);
  }

  // Import conversations from JSON
  public static importConversations(jsonData: string): boolean {
    try {
      const conversations: Conversation[] = JSON.parse(jsonData);
      
      // Validate structure
      if (!Array.isArray(conversations)) return false;
      
      // Validate each conversation
      for (const convo of conversations) {
        if (!convo.id || !convo.title || !Array.isArray(convo.messages)) {
          return false;
        }
      }
      
      localStorage.setItem(this.CONVERSATIONS_KEY, jsonData);
      return true;
    } catch (e) {
      console.error('Failed to import conversations:', e);
      return false;
    }
  }

  // Get conversation statistics
  public static getStatistics(): {
    totalConversations: number;
    totalMessages: number;
    activeConversation: string | null;
    storageSize: number;
  } {
    const conversations = this.getAllConversations();
    const activeConvo = this.getCurrentConversation();
    
    return {
      totalConversations: conversations.length,
      totalMessages: conversations.reduce((sum, convo) => sum + convo.messages.length, 0),
      activeConversation: activeConvo?.title || null,
      storageSize: new Blob([JSON.stringify(conversations)]).size
    };
  }

  // Cleanup old conversations (older than 30 days)
  public static cleanupOldConversations(days: number = 30): number {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    const cutoffISOString = cutoffDate.toISOString();
    const conversations = this.getAllConversations();
    const filtered = conversations.filter(convo => convo.createdAt > cutoffISOString);
    
    const removedCount = conversations.length - filtered.length;
    
    if (removedCount > 0) {
      localStorage.setItem(this.CONVERSATIONS_KEY, JSON.stringify(filtered));
    }
    
    return removedCount;
  }
}

// Hook for React components
export const useChatPersistence = () => {
  return ChatPersistence;
};